import React, { lazy, Suspense, useState } from 'react';
import { Pentagon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { GeoLocation } from 'components/GeoLocationComponent/GeoLocationComponent';
import { getActionGeoJsonGeometry } from '../utils/actionGeolocation';
import {
  isAreaGeometry,
  type ActionAreaFilter,
} from '../utils/actionSpatialFilter';

/** Drawing pulls in ArcGIS, which the table otherwise has no reason to load. */
const GeoLocationComponent = lazy(
  () => import('components/GeoLocationComponent/GeoLocationComponent')
);

type ActionAreaFilterControlProps = {
  filter: ActionAreaFilter | null;
  onChange: (filter: ActionAreaFilter | null) => void;
  /** Rendered next to the control once a filter is active. */
  summary?: string;
  /** Draw UI only — used inside the Actions filter popover. */
  embedded?: boolean;
};

/**
 * "Show me the tasks in this area."
 *
 * The filtering itself happens in the browser against geometry the list already
 * carried, which is why this is a view control rather than a query: it can only
 * see loaded actions. The server-side version needs PostGIS columns the action
 * tables do not have.
 */
export const ActionAreaFilterControl: React.FC<
  ActionAreaFilterControlProps
> = ({ filter, onChange, summary, embedded = false }) => {
  const [open, setOpen] = useState(false);
  const [drawn, setDrawn] = useState<GeoLocation | undefined>(undefined);

  const handleDraw = (location: GeoLocation) => {
    setDrawn(location);
    const geometry = getActionGeoJsonGeometry(location);
    if (!isAreaGeometry(geometry)) {
      // A dropped pin has no inside, so there is nothing to filter by yet.
      onChange(null);
      return;
    }
    onChange({ geometry, label: location.address || 'Drawn area' });
  };

  const clear = () => {
    setDrawn(undefined);
    onChange(null);
    setOpen(false);
  };

  const drawFields = (
    <>
      <p className="tw:mb-2 tw:text-xs tw:text-foreground">
        Draw an area to show only the tasks inside it. Tasks without a location
        are hidden while the filter is on.
      </p>
      <Suspense
        fallback={
          <p className="tw:mb-0 tw:text-xs tw:text-foreground">
            Loading the map…
          </p>
        }
      >
        <GeoLocationComponent location={drawn} setLocation={handleDraw} />
      </Suspense>
      <div className="tw:mt-3 tw:flex tw:gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clear}
          disabled={!filter}
        >
          Clear filter
        </Button>
        {!embedded ? (
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        ) : null}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div>
        {filter && summary ? (
          <p
            className="tw:mb-2 tw:text-xs tw:text-muted-foreground"
            title={filter.label}
          >
            {summary}
          </p>
        ) : null}
        {drawFields}
      </div>
    );
  }

  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      {filter && summary ? (
        <Badge variant="secondary" title={filter.label}>
          {summary}
        </Badge>
      ) : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="sm"
              title="Filter tasks by area"
            />
          }
        >
          <Pentagon className="tw:size-4" />
          {filter ? 'Area filter on' : 'Filter by area'}
        </PopoverTrigger>
        <PopoverContent className="dark tw:w-96">{drawFields}</PopoverContent>
      </Popover>
    </div>
  );
};

export default ActionAreaFilterControl;
